import {
  tracks, ordtakOfDay, overallGoal, labelForLevel,
  type CefrSubLevel,
} from '../data/sample-today.ts';
import { CAN_DOS, CEFR_ORDER } from '../data/candos.ts';
import { loadSettings, loadCanDoStates, type CanDoState } from '../data/stores.ts';
import { backgroundOfDay, backgroundForSeed } from '../backgrounds.ts';
import { icons } from '../icons.ts';
import { navigate } from '../router.ts';
import type { Route } from '../router.ts';

const trackIcon: Record<string, string> = {
  vocab: icons.vocab,
  grammar: icons.grammar,
  pron: icons.pron,
  listen: icons.listen,
  task: icons.task,
};

const trackRoute: Record<string, Route> = {
  vocab: 'vocabulaire',
  grammar: 'grammaire',
  pron: 'prononciation',
  listen: 'ecoute',
  task: 'tache',
};

export function renderAccueil(): HTMLElement {
  const root = document.createElement('section');
  root.className = 'flex flex-col gap-6 max-w-5xl';
  root.innerHTML = '<p class="text-[color:var(--color-ink-muted)] mt-12">Chargement…</p>';
  void init(root);
  return root;
}

async function init(root: HTMLElement): Promise<void> {
  const [settings, candoStates] = await Promise.all([loadSettings(), loadCanDoStates()]);
  root.innerHTML = '';
  root.append(renderHeader());
  root.append(renderHeroCard(settings.currentLevel));
  root.append(renderTracksRow());
  root.append(renderCanDos(settings.currentLevel, candoStates));
  root.append(renderFooter());
}

function renderHeader(): HTMLElement {
  const h = document.createElement('header');
  h.className = 'flex items-baseline justify-between';
  h.innerHTML = `
    <div>
      <h1 class="text-4xl font-semibold tracking-tight">Aujourd’hui</h1>
      <p class="text-sm text-[color:var(--color-ink-muted)] mt-1 flex items-center gap-2">
        <span>📍 Stavanger, Norge</span>
      </p>
    </div>
    <div class="flex items-center gap-2 text-sm">
      <span class="w-8 h-8 rounded-full bg-[color:var(--color-fjord-600)] text-white flex items-center justify-center text-xs font-medium">B</span>
      <span>Ben</span>
    </div>
  `;
  return h;
}

function renderHeroCard(level: CefrSubLevel): HTMLElement {
  const c = document.createElement('div');
  c.className = 'relative overflow-hidden rounded-xl border border-[color:var(--color-border)] min-h-[240px]';
  c.innerHTML = `
    <img
      src="${backgroundOfDay()}"
      alt="Paysage norvégien du jour"
      class="absolute inset-0 w-full h-full object-cover"
      loading="eager"
      decoding="async"
    />
    <div class="absolute inset-0 bg-gradient-to-r from-[color:var(--color-surface-elevated)] from-30% via-[color:var(--color-surface-elevated)]/85 via-55% to-transparent"></div>
    <div class="relative z-10 p-6 max-w-[60%]">
      <h2 class="text-2xl font-medium mb-1">Hei, Ben&nbsp;!</h2>
      <p class="text-[color:var(--color-ink-muted)] mb-4">Prêt pour votre séance de norvégien&nbsp;?</p>
      <div class="flex items-center gap-3 flex-wrap">
        <span class="text-sm text-[color:var(--color-ink-muted)]">Niveau actuel&nbsp;:</span>
        <span class="px-3 py-1 rounded-md bg-[color:var(--color-fjord-600)] text-white font-mono text-sm font-medium">${level}</span>
        <span class="text-sm">— ${labelForLevel(level)}</span>
      </div>
      <p class="mt-3 text-xs text-[color:var(--color-ink-muted)]">🎯 Objectif global&nbsp;: ${overallGoal}.</p>
    </div>
  `;
  return c;
}

function renderTracksRow(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.innerHTML = `
    <h3 class="text-sm font-medium mb-3">Mon parcours du jour <span class="text-[color:var(--color-ink-muted)] font-normal">— 5 compétences, chaque jour</span></h3>
  `;
  const row = document.createElement('div');
  row.className = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3';

  for (const [i, t] of tracks.entries()) {
    const card = document.createElement('article');
    card.className = 'relative overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] flex flex-col transition-transform hover:-translate-y-0.5 hover:shadow-lg';
    card.innerHTML = `
      <div class="relative h-20 overflow-hidden">
        <img src="${backgroundForSeed(t.id)}" alt="" class="w-full h-full object-cover" loading="lazy" decoding="async" />
        <div class="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[color:var(--color-surface-elevated)] to-transparent"></div>
        <span class="absolute top-2 right-2 font-mono text-[10px] tracking-wider px-1.5 py-0.5 rounded bg-black/40 text-white backdrop-blur-sm">${String(i + 1).padStart(2, '0')}</span>
      </div>
      <div class="px-4 pb-4 -mt-7 relative z-10 flex flex-col flex-1">
        <div class="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md mb-3" style="background: var(--tint-${t.tint}-bg); color: var(--tint-${t.tint}-ink);">
          <span class="w-6 h-6 inline-block">${trackIcon[t.id] ?? ''}</span>
        </div>
        <h4 class="font-semibold text-sm leading-tight">${t.title}</h4>
        <p class="text-[11px] text-[color:var(--color-ink-muted)] uppercase tracking-wide">${t.subtitle}</p>
        <p class="text-xs mt-2 leading-snug">${t.detail}</p>
        <div class="flex items-center gap-2 mt-3 text-[11px] text-[color:var(--color-ink-muted)]">
          <span>${t.minutes} min</span>
          <span aria-hidden>·</span>
          <span class="font-mono px-1.5 py-0.5 rounded text-[10px]" style="background: var(--tint-${t.tint}-bg); color: var(--tint-${t.tint}-ink);">${t.cefrTag}</span>
        </div>
        <button class="mt-auto pt-3 self-start text-xs font-medium">
          <span class="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[color:var(--color-fjord-600)] text-white hover:bg-[color:var(--color-fjord-700)] transition-colors">
            <span class="w-3 h-3 inline-block">${icons.play}</span> Commencer
          </span>
        </button>
      </div>
    `;
    const route = trackRoute[t.id];
    if (route) {
      card.querySelector('button')!.addEventListener('click', () => navigate(route));
    }
    row.append(card);
  }

  wrap.append(row);
  return wrap;
}

function renderCanDos(level: CefrSubLevel, states: Map<string, CanDoState>): HTMLElement {
  // Pick the most relevant can-dos: current level + one below, prioritising in-progress + todo.
  const levelIdx = CEFR_ORDER.indexOf(level);
  const focusLevels = new Set(CEFR_ORDER.slice(Math.max(0, levelIdx - 1), levelIdx + 1));
  const focus = CAN_DOS.filter((c) => focusLevels.has(c.cefr));
  const ordered = focus
    .map((c) => ({ c, status: states.get(c.id)?.status ?? 'todo' as const }))
    .sort((a, b) => statusRank(a.status) - statusRank(b.status))
    .slice(0, 5);

  const doneCount = [...states.values()].filter((s) => s.status === 'done').length;

  const wrap = document.createElement('section');
  const subtitle = doneCount === 0
    ? "Vos premiers acquis apparaîtront ici dès qu'une compétence sera validée."
    : `${doneCount} compétence${doneCount > 1 ? 's' : ''} déjà acquise${doneCount > 1 ? 's' : ''} dans votre parcours.`;
  wrap.innerHTML = `
    <h3 class="text-sm font-medium mb-1">Focus de la semaine</h3>
    <p class="text-xs text-[color:var(--color-ink-muted)] mb-3">${subtitle}</p>
  `;
  const list = document.createElement('ul');
  list.className = 'divide-y divide-[color:var(--color-border)] rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] overflow-hidden';

  for (const { c, status } of ordered) {
    const li = document.createElement('li');
    li.className = 'flex items-center justify-between gap-3 px-4 py-3';
    const tick = status === 'done'
      ? '<span class="w-5 h-5 rounded-full bg-[color:var(--color-sage)] text-white flex items-center justify-center text-xs">✓</span>'
      : status === 'in-progress'
        ? '<span class="w-5 h-5 rounded-full bg-[color:var(--color-stavanger)] text-[color:var(--color-fjord-900)] flex items-center justify-center text-xs">◐</span>'
        : '<span class="w-5 h-5 rounded-full border-2 border-[color:var(--color-border)]"></span>';
    const statusBadge = status === 'in-progress'
      ? '<span class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-[color:var(--color-stavanger)] text-[color:var(--color-fjord-900)]">en cours</span>'
      : '';
    li.innerHTML = `
      <div class="flex items-center gap-3 flex-1">
        ${tick}
        <span class="text-sm ${status === 'done' ? 'text-[color:var(--color-ink-muted)] line-through' : ''}">${c.text}</span>
      </div>
      <div class="flex items-center gap-2">
        ${statusBadge}
        <span class="text-[10px] font-mono px-2 py-0.5 rounded bg-[color:var(--color-fjord-50)] text-[color:var(--color-fjord-700)]">${c.cefr}</span>
      </div>
    `;
    list.append(li);
  }

  const more = document.createElement('a');
  more.href = '#/acquis';
  more.className = 'inline-block mt-3 text-xs text-[color:var(--color-fjord-600)] hover:underline';
  more.textContent = 'Voir tous mes acquis →';

  wrap.append(list);
  wrap.append(more);
  return wrap;
}

function statusRank(s: CanDoState['status']): number {
  return s === 'in-progress' ? 0 : s === 'todo' ? 1 : 2;
}

function renderFooter(): HTMLElement {
  const f = document.createElement('footer');
  f.className = 'mt-6 pt-6 border-t border-[color:var(--color-border)] flex items-start justify-between gap-6 text-xs';
  const ordtak = ordtakOfDay();
  f.innerHTML = `
    <blockquote class="italic text-[color:var(--color-ink-muted)] max-w-md">
      « ${ordtak.norwegian} »
      <span class="block not-italic mt-1 opacity-80">(${ordtak.french})</span>
    </blockquote>
    <div class="text-right text-[color:var(--color-ink-muted)]">
      <div class="font-medium text-[color:var(--color-ink)] flex items-center gap-2 justify-end">
        <span>🇳🇴</span><span>Bokmål · siddis (Stavanger)</span>
      </div>
      <div>Synthèse vocale&nbsp;: voix nb-NO du système</div>
    </div>
  `;
  return f;
}
